/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import { useLocation } from 'js/lib/useRouter';

import {
  Button,
  CardMetadata,
  CardSkills,
  Grid,
  ProductCard,
  Skeleton,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';
import { CertificateCircledIcon, QuizCircledIcon, ReadingCircledIcon, VideoCircledIcon } from '@coursera/cds-icons';
import type { ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  EntWebsiteBlockCourseSearchWidgetV2Query,
  EntWebsiteCourseSearchWidgetV2Fragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/ent-website/components/CourseSearch/constants';
import CourseSearchV2 from 'bundles/ent-website/components/CourseSearchV2/CourseSearchV2';
import { CHIP_EVENT_NAME_MAPPING, COURSERA_TEAMS_POPULAR_DOMAINS_MAPPING } from 'bundles/ent-website/constants/misc';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import { entWebsiteBlockCourseSearchWidgetV2Query } from 'bundles/ent-website/queries/EntWebsiteCourseSearchWidgetV2';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import { formatNumber } from 'bundles/enterprise-ui/utils';
import ProductCardReviews from 'bundles/product-card/components/ProductCardRatings';
import { eventingV3ProductType, extractEventId } from 'bundles/product-card/components/utils/productCardV2Utils';
import {
  LEARNING_PRODUCTS_ENUM_MAP,
  PRODUCT_DURATIONS_STRING,
  getTranslatedDifficulty,
  getTranslatedDurations,
  getTranslatedProductName,
} from 'bundles/search-common/constants';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';

import _t from 'i18n!nls/ent-website';

import StatsInfo from './components/StatsInfo';
import { convertToProductName } from './utils';

export type CourseSearchWidgetV2Props = BlockModuleInnerProps<EntWebsiteCourseSearchWidgetV2Fragment>;

const styles = {
  searchWidgetContainer: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: var(--cds-spacing-300);
    ${breakpoints.down('md')} {
      flex-direction: column;
    }
  `,
  statsContainer: css`
    margin-bottom: var(--cds-spacing-200);
    text-align: right;
    ${breakpoints.down('md')} {
      text-align: unset;
    }
  `,
  headLine: css`
    width: 30%;
    padding-top: var(--cds-spacing-200);
    ${breakpoints.down('md')} {
      width: unset;
      padding-top: unset;
    }
  `,
  resultsContainer: css`
    margin-bottom: var(--cds-spacing-200);
    min-height: 422px;
    gap: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      justify-content: center;
    }
  `,
  resultsCountText: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  contentfulCtaButton: css`
    margin-right: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-right: 0;
      margin-bottom: var(--cds-spacing-200);
      width: 100%;
    }
  `,
  viewAllButton: css`
    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,
  productCard: css`
    width: 320px !important;
    margin-right: 4px;
    ${breakpoints.down('xs')} {
      width: 100% !important;
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  ctaContainer: css`
    ${breakpoints.down('sm')} {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  `,
};

function getGetRedirectionParams(pathname: string) {
  if (pathname.startsWith('/business/teams')) {
    return {
      sourceQueryParams: 'teams',
      srpRedirectionPath: '/business/teams/search',
    };
  }

  return {
    sourceQueryParams: 'business',
    srpRedirectionPath: '/business/search',
  };
}

export function CourseSearchWidgetV2({ content, assembly }: CourseSearchWidgetV2Props) {
  const [searchQuery, setSearchQuery] = React.useState(COURSERA_TEAMS_POPULAR_DOMAINS_MAPPING[0]?.name ?? '');
  const track = useTracker();
  const location = useLocation();

  const redirectionParams = getGetRedirectionParams(location.pathname);

  const entSearch = React.useMemo(
    () => ({
      id: 'entSearch',
      entityType: 'PRODUCTS' as const,
      limit: NUMBER_OF_SEARCH_RESULTS,
      searchContext: {
        searchInWebEnabledSalesCatalog: {},
      },
      query: searchQuery,
      maxValuesPerFacet: 1000,
      facetFilters: [
        ['productTypeDescription:-Short Form Content Video'],
        ['productTypeDescription:-Short Form Content Lesson'],
      ],
    }),
    [searchQuery]
  );

  const { isLoading, results: searchResults } = useSearchQuery({
    entSearch,
  });

  const onSearch = React.useCallback<(v: string) => void>(
    (v: string) => {
      setSearchQuery(v);
      track('perform_search', {
        searchedQuery: v,
        searchContextDescription: 'enterprise_website_search_widget',
      });
    },
    [track]
  );

  const onSearchInputFocus = React.useCallback<() => void>(() => {
    track('click_text_input', {
      textInput: {
        name: 'ent_website_search_widget_input',
      },
    });
  }, [track]);

  const onSearchInputChange = React.useCallback<(v: string) => void>(
    (v: string) => {
      track('change_text_input', {
        textInput: {
          name: 'ent_website_search_widget_input',
          value: v,
        },
      });
    },
    [track]
  );

  const handleCtaClick = () => {
    track('click_button', {
      button: {
        name: 'ent_website_search_widget_contentful_cta',
      },
    });
  };
  const handleViewAllSearchResultsClick = () => {
    track('click_button', {
      button: {
        name: 'ent_website_search_widget_view_all_search_results',
      },
    });
  };

  const onChipClick = React.useCallback<(domainId: string) => void>(
    (domainId: string) => {
      if (CHIP_EVENT_NAME_MAPPING[domainId]) {
        track('click_button', {
          button: {
            name: CHIP_EVENT_NAME_MAPPING[domainId],
          },
        });
      }
    },
    [track]
  );

  const totalCount = searchResults?.[0]?.pagination.totalElements ?? null;
  const isTotalCountLessThanLimit = totalCount === null ? true : totalCount < NUMBER_OF_SEARCH_RESULTS;
  const formattedTotalCount = `${formatNumber(totalCount)}${totalCount !== null && totalCount >= 10000 ? '+' : ''}`;

  const results = React.useMemo(() => {
    if (isLoading) {
      return Array.from(new Array(NUMBER_OF_SEARCH_RESULTS)).map((_, idx) => (
        <ProductCard
          css={styles.productCard}
          variant="grid"
          productType="course"
          key={idx}
          loading
          data-testid={`product-card-loading-${idx}`}
        />
      ));
    }
    if (!searchResults?.[0]?.elements) {
      return [];
    }

    return (searchResults[0].elements as SearchProductHit[]).map((element, index) => {
      return (
        <Grid item key={element.id} css={styles.productCard}>
          <ProductCard
            onClick={() => {
              const eventId = extractEventId(element.id);
              const { name, url, productType } = element;

              if (!eventId || !name || !url) {
                return;
              }

              track('click_product_card', {
                product: {
                  id: eventId,
                  name,
                  // * The type casting is because of legacy reasons, as eventingV3ProductType expects a LEARNING_PRODUCTS value which is now replaced in favor of PRODUCT_TYPES
                  type: eventingV3ProductType(
                    productType as (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]
                  ) as ProductType,
                  slug: url,
                },
                productCard: {
                  index,
                  algoliaIndexName: searchResults[0]?.source?.indexName ?? '',
                },
              });
            }}
            variant="grid"
            productType="course"
            previewImageSrc={element.imageUrl!}
            title={{
              name: element.name!,
              linkProps: {
                href: `${element.url!}${element.url!.includes('?') ? '&' : '?'}source=${
                  redirectionParams.sourceQueryParams
                }`,
                target: '_blank',
              },
            }}
            partners={element.partners?.map((partner, idx) => ({
              name: partner,
              logoUrl: element.partnerLogos?.[idx],
            }))}
            body={<CardSkills skills={element.skills!} />}
            footer={
              <>
                <ProductCardReviews avgProductRating={element.avgProductRating!} />
                <CardMetadata
                  metadata={[
                    getTranslatedDifficulty(element.productDifficultyLevel!),

                    element.productType
                      ? getTranslatedProductName(
                          convertToProductName(LEARNING_PRODUCTS_ENUM_MAP[element.productType])
                        ) ?? ''
                      : '',
                    element.productDuration
                      ? getTranslatedDurations(PRODUCT_DURATIONS_STRING[element.productDuration]!) ?? ''
                      : '',
                  ]}
                />
              </>
            }
          />
        </Grid>
      );
    });
  }, [isLoading, redirectionParams.sourceQueryParams, searchResults, track]);

  const contentPanel = () => (
    <div>
      <div css={styles.searchWidgetContainer}>
        <Typography2 component="h2" color="inherit" variant="titleLargeCompact" css={styles.headLine}>
          {content.headline}
        </Typography2>
        <div>
          <div css={styles.statsContainer}>
            <StatsInfo title="courses" count={content.coursesCount} Icon={ReadingCircledIcon} />
            <StatsInfo title="certificates" count={content.certificatesCount} Icon={CertificateCircledIcon} />
            <StatsInfo title="hands on labs" count={content.handsOnLabsCount} Icon={QuizCircledIcon} />
            <StatsInfo title="clips" count={content.clipsCount} Icon={VideoCircledIcon} hideSeparator />
          </div>
          <CourseSearchV2
            currentQuery={searchQuery}
            onSearch={onSearch}
            onChipClick={onChipClick}
            onFocus={onSearchInputFocus}
            onChange={onSearchInputChange}
          />
        </div>
      </div>
      <Typography2 component="p" variant="subtitleMedium" css={styles.resultsCountText}>
        {isLoading ? (
          <Skeleton width={50} />
        ) : (
          <>
            {_t(`Showing #{currentCount} of #{totalCount} results for`, {
              currentCount: isTotalCountLessThanLimit ? formatNumber(totalCount) : NUMBER_OF_SEARCH_RESULTS,
              totalCount: formattedTotalCount,
            })}
            &nbsp;&quot;{searchQuery}&quot;
          </>
        )}
      </Typography2>
      <Grid container css={styles.resultsContainer} justifyContent="flex-start" xs md={12}>
        {results}
      </Grid>
      <div css={styles.ctaContainer}>
        {!isLoading && content.searchResultCta && (
          <ContentfulButton
            component="a"
            variant="primary"
            href={content.searchResultCta.ctaTarget}
            module={content}
            position={0}
            onClick={handleCtaClick}
            css={styles.contentfulCtaButton}
          >
            {content.searchResultCta.ctaText}
          </ContentfulButton>
        )}
        {!isTotalCountLessThanLimit && (
          <Button
            component={Link}
            variant="secondary"
            to={{
              pathname: redirectionParams.srpRedirectionPath,
              query: { query: searchQuery },
            }}
            onClick={handleViewAllSearchResultsClick}
            css={styles.viewAllButton}
            loading={isLoading}
          >
            {_t('View all #{totalCount} results', {
              totalCount: formattedTotalCount,
            })}
          </Button>
        )}
      </div>
    </div>
  );
  return <BlockLayout assembly={{ ...noImage(assembly), theme: 'grey-bg' }} renderContent={contentPanel} />;
}

export const BlockCourseSearchWidgetV2 = withBlockQuery<
  EntWebsiteCourseSearchWidgetV2Fragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockCourseSearchWidgetV2Query
>(entWebsiteBlockCourseSearchWidgetV2Query)(CourseSearchWidgetV2);
